import { useEffect, useRef, useState } from 'react';
import useWebSocket from '../../api/websocket';
import api from '../../api/client';
import StatusBadge from '../common/StatusBadge';
import DurationDisplay from '../common/DurationDisplay';

// Bloque de programa para la tira de programación dentro de LIVE:
// etiqueta arriba (horario, o "Programa actual · horario" en rojo) + foto y
// nombre abajo. Igual estilo que "En vivo ahora" en /programacion.
function ProgBlock({ p, highlight = false }) {
  const label = highlight ? `Programa actual · ${p?.range || ''}` : (p?.range || '');
  const sub = [p?.dj, p?.when && p.when !== 'hoy' ? p.when : null].filter(Boolean).join(' · ');
  return (
    <div className={`rounded-lg border p-1.5 ${
      highlight ? 'border-error-600 bg-error-900/25' : 'border-dark-700/60 bg-dark-800/25'
    }`}>
      <div className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider ${
        highlight ? 'text-error-300' : 'text-dark-500'
      }`}>
        {highlight && (
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-error-400 opacity-75"></span>
            <span className="relative inline-flex h-2 w-2 rounded-full bg-error-500"></span>
          </span>
        )}
        <span className="truncate">{label}</span>
      </div>
      <div className="mt-1 flex items-center gap-2">
        {p?.image ? (
          <img
            src={p.image}
            alt=""
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
            className="h-10 w-10 shrink-0 rounded-md border border-dark-600 object-cover"
          />
        ) : (
          <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md border text-base ${
            highlight ? 'border-error-700/60 bg-error-900/40' : 'border-dark-700 bg-dark-800'
          }`}>🎙️</span>
        )}
        <span className="min-w-0">
          <b className={`block truncate text-sm font-bold ${highlight ? 'text-white' : 'text-dark-100'}`}>
            {p?.program || '—'}
          </b>
          {sub && <span className="block truncate text-[10px] text-dark-400">{sub}</span>}
        </span>
      </div>
    </div>
  );
}

export default function NowPlaying({ compact = false, data, live, schedule }) {
  const [nowPlaying, setNowPlaying] = useState(data || null);
  const [loading, setLoading] = useState(!data);
  const [seeking, setSeeking] = useState(false);

  // Tira de programación: refs para auto-centrar el programa actual al abrir
  const progListRef = useRef(null);
  const currentProgRef = useRef(null);
  useEffect(() => {
    const list = progListRef.current;
    const cur = currentProgRef.current;
    if (list && cur) {
      list.scrollTop = Math.max(0, cur.offsetTop - list.clientHeight / 2 + cur.clientHeight / 2);
    }
  }, [schedule?.sequence?.length, schedule?.current?.program]);

  const doSeek = async (delta) => {
    setSeeking(true);
    try {
      await api.post('/api/playback/seek', { delta });
    } catch (e) {
      console.error('[NowPlaying] Seek error:', e);
    } finally {
      setTimeout(() => setSeeking(false), 400);
    }
  };

  const barRef = useRef(null);
  const [dragPos, setDragPos] = useState(null);
  const [dragging, setDragging] = useState(false);

  const fmtTime = (s) => {
    s = Math.max(0, Math.round(Number(s) || 0));
    return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  };

  const posFromEvent = (e) => {
    const rect = barRef.current?.getBoundingClientRect();
    if (!rect || !rect.width) return 0;
    const fraction = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    return fraction * total;
  };

  const handlePointerDown = (e) => {
    if (!total) return;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
    setDragging(true);
    setDragPos(posFromEvent(e));
  };

  const handlePointerMove = (e) => {
    if (!dragging) return;
    setDragPos(posFromEvent(e));
  };

  const handlePointerUp = (e) => {
    if (!dragging) return;
    const target = posFromEvent(e);
    setDragging(false);
    setDragPos(null);
    const delta = Math.round(target - elapsed);
    if (delta !== 0) doSeek(delta);
  };

  useEffect(() => {
    setNowPlaying(data);
    setLoading(false);
  }, [data]);

  const { subscribe } = useWebSocket((ws) => {
    if (ws.type === 'now_playing') {
      setNowPlaying(ws.payload);
      setLoading(false);
    }
  });

  useEffect(() => {
    const unsub = subscribe('now_playing', (ws) => {
      setNowPlaying(ws.payload);
      setLoading(false);
    });

    if (!nowPlaying && !data) {
      api.get('/api/dashboard').then((res) => {
        setNowPlaying(res.data.now_playing);
        setLoading(false);
      }).catch(() => setLoading(false));
    }

    return unsub;
  }, [subscribe, nowPlaying, data]);

  const liveTrack = live?.currentTrack || null;
  const elapsed = Math.round(live?.elapsed || 0);
  const total = liveTrack?.duration_seconds || 0;
  const displayPos = dragging && dragPos != null ? dragPos : elapsed;

  const title = liveTrack?.title || nowPlaying?.title;
  const artist = liveTrack?.artist || nowPlaying?.artist;

  if (loading) {
    return (
      <div className="rounded-xl bg-dark-850 border border-dark-700 p-4 shadow-xl">
        <p className="text-sm text-dark-500">Cargando...</p>
      </div>
    );
  }

  if (!title) {
    return (
      <div className="rounded-xl bg-dark-850 border border-dark-700 p-4 shadow-xl">
        <p className="text-sm text-dark-500">Sin reproducción en el aire</p>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="flex items-center gap-4 rounded-xl bg-dark-850 border border-dark-700 px-4 py-3 shadow-xl">
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-dark-800 text-2xl">
          🎵
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-dark-100 truncate">{artist} - {title}</p>
          <p className="text-xs text-dark-500">{nowPlaying?.program_name || 'Automático'}</p>
        </div>
        <StatusBadge status="playing" size="sm" />
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-dark-850 border border-dark-700 p-4 shadow-xl">
      <div className="flex items-center gap-3">
        {/* Foto del programa que está al aire (fallback: ícono) */}
        {schedule?.current?.image ? (
          <img
            src={schedule.current.image}
            alt={schedule.current.program || ''}
            title={schedule.current.program || ''}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
            className="h-11 w-11 flex-shrink-0 rounded-lg border border-dark-600 object-cover"
          />
        ) : (
          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-dark-800 text-2xl">
            🎵
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-medium uppercase text-success-400">LIVE</span>
            <StatusBadge status="playing" size="sm" />
          </div>
          <p className="text-base font-bold text-dark-100 truncate">{title}</p>
          <p className="text-xs text-dark-300 truncate">{artist}</p>
          <p className="text-[10px] text-dark-500 truncate">
            en {liveTrack?.program || nowPlaying?.program_name || 'AUTODJ RADIO'}
          </p>
        </div>
      </div>

      {/* Línea de tiempo: arrastra o haz clic para moverte en el track */}
      {total > 0 ? (
        <div className="mt-3">
          <div className="flex items-center justify-between text-xs text-dark-400 mb-1">
            <span className="font-mono">{fmtTime(displayPos)}</span>
            <span className="text-dark-500">{fmtTime(total)}</span>
            <span className="font-mono text-primary-300">-{fmtTime(Math.max(0, total - displayPos))}</span>
          </div>
          <div
            ref={barRef}
            role="slider"
            aria-label="Línea de tiempo del track en el aire"
            tabIndex={0}
            className="relative h-3 w-full cursor-pointer touch-none rounded-full bg-dark-700 overflow-hidden select-none"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerUp}
          >
            <div
              className="h-full rounded-full bg-primary-500"
              style={{ width: `${Math.min(100, Math.max(0, (displayPos / total) * 100))}%` }}
            />
            <div
              className="absolute top-1/2 h-4 w-4 -translate-y-1/2 -translate-x-1/2 rounded-full bg-white shadow pointer-events-none"
              style={{ left: `${Math.min(100, Math.max(0, (displayPos / total) * 100))}%` }}
            />
          </div>
          <div className="mt-1 flex justify-between text-[11px] text-dark-500">
            <span>arrastra o haz clic para adelantar / retroceder</span>
            {seeking && <span className="text-primary-300">aplicando…</span>}
          </div>
        </div>
      ) : (
        nowPlaying?.duration_seconds && (
          <p className="mt-2 text-xs text-dark-500">
            Duración: <DurationDisplay seconds={nowPlaying.duration_seconds} />
          </p>
        )
      )}

      {/* Programación del día: tira scrolleable con lo que pasó y lo que sigue.
          El programa actual (rojo) queda auto-centrado al abrir. */}
      {(schedule?.sequence || []).length > 0 && (
        <div className="mt-3 border-t border-dark-700/70 pt-2.5">
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wider text-dark-500">Programación (±12 horas)</span>
            <span className="text-[9px] text-dark-600">deslizá para ver antes / después</span>
          </div>
          <div ref={progListRef} className="relative h-64 space-y-1.5 overflow-y-auto pr-1">
            {(schedule?.sequence || []).map((p, i) => (
              <div key={i} ref={p.is_current ? currentProgRef : undefined}>
                <ProgBlock p={p} highlight={!!p.is_current} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
